import {Injectable} from '@angular/core';
import type {AngularFirestoreCollection, QueryFn} from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import {parseLineID} from '@vat/engine/rules/minderung';
import {LandlordChangeService} from 'app/modules/admin/properties/features/landlord_change/services/landlord-change.service';
import type {LandlordChange} from 'app/modules/admin/properties/features/landlord_change/models/landlordChange.interface';
import {BookingPaymentsService} from 'app/modules/admin/booking/services/bookingPayments.service';
import {BookingService} from 'app/modules/admin/booking/services/booking.service';
import type {Booking} from 'app/modules/admin/booking/models/booking';
import type {BookingPayment} from 'app/modules/admin/booking/features/payments/models/bookingPayment';
import {ExpensesService} from 'app/modules/admin/expenses/services/expenses.service';
import type {Expense} from 'app/modules/admin/expenses/models/expenses.interface';
import {PeriodsService} from '@periods/services/periods.service';
import type {PropertyUnitPeriod} from '@periods/models/property.unit.period.interface';
import {PropertiesService} from '@properties/services/properties.service';
import type {Property} from '@properties/models/property.interface';
import {UnitsService} from '@units/services/units.service';
import type {PropertyUnit} from '@units/models/unit.interface';
import {AuthService} from '@shared/services/auth.service';
import {createProfileAt} from '@vat/engine/tax-profile';
import type {
    BookingLite,
    ExpenseLite,
    LandlordTaxProfile,
    Monitor19Input,
    OwnershipWindow,
    PaymentLite,
    PendingLandlordChange,
    PeriodLite,
    PropertyVatProfile,
    UnitLite,
    VatAdjustment,
    VatEngineInput,
    VatEngineRange,
    VatTaxStatus
} from '@vat/engine/types';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {VAT_LOADER_COPY} from '../vat-copy';
import {resolvePropertyVatProfile} from './property-vat-profile.util';
import {toBookingLite, toExpenseLite, toOwnership, toPaymentLite, toUnitLite} from './vat-lite.adapters';
import {
    BATCH_SIZE,
    bookingIDsWithHaben,
    buildLoadWindow,
    chunk,
    IN_CHUNK_SIZE,
    LoadWindow,
    mergeUniqueByID,
    missingBookingIDs,
    monitorRanges,
    needsMonitor19,
    PARALLEL_PROPERTIES,
    PAYMENT_ARTS_IN,
    propertyLabel,
    runLimited,
    taxableTurnoverOfBookings
} from './vat-loader.util';
import {toPeriodLite} from './vat-period-lite.adapter';
import {VatPropertyProfilesService} from './vat-property-profiles.service';

export interface VatLoadProgress {
    done: number;
    total: number;
    label: string;
}

export interface VatLandlordProperties {
    ids: string[];
    properties: Map<string, Property>;
}

export interface VatLoadedData {
    propertyIDs: string[];
    propertyLabels: Record<string, string>;
    ownership: OwnershipWindow[];
    pendingChanges: PendingLandlordChange[];
    periods: PeriodLite[];
    units: UnitLite[];
    propertyProfiles: Record<string, PropertyVatProfile>;
    bookings: BookingLite[];
    payments: PaymentLite[];
    priorPayments: PaymentLite[];
    expenses: ExpenseLite[];
    monitor19Input: Monitor19Input | null;
}

export interface BuildEngineInputParams {
    landlordID: string;
    range: VatEngineRange;
    profile: LandlordTaxProfile;
    adjustments: VatAdjustment[];
    now: Date;
}

interface PropertySlice {
    propertyID: string;
    periods: PeriodLite[];
    units: UnitLite[];
    bookings: BookingLite[];
    payments: PaymentLite[];
    expenses: ExpenseLite[];
    ownership: OwnershipWindow[];
    pending: PendingLandlordChange[];
    profile: PropertyVatProfile;
}

/**
 * Lädt je Eigentümer und Zeitraum alle Engine-Eingaben (Spec 5) – ausschließlich in live existierenden
 * Query-Formen (keine neuen Indizes), vier Immobilien parallel, Fortschritt als BehaviorSubject.
 * Component-provided: jede Seite bekommt frische stateful Services (BookingService,
 * BookingPaymentsService) ohne fremden parentPath.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable()
export class VatDataLoaderService {

    readonly progress$ = new BehaviorSubject<VatLoadProgress>({done: 0, total: 0, label: ''});

    constructor(
        private authService: AuthService,
        private propertiesService: PropertiesService,
        private landlordChangeService: LandlordChangeService,
        private periodsService: PeriodsService,
        private unitsService: UnitsService,
        private bookingService: BookingService,
        private bookingPaymentsService: BookingPaymentsService,
        private expensesService: ExpensesService,
        private vatPropertyProfilesService: VatPropertyProfilesService,
    ) {
    }

    /** Immobilien des Eigentümers: aktuelle (`landlord.id`) plus frühere (`landlordChange.beforeLandlord.id`). */
    async resolveProperties(landlordID: string): Promise<VatLandlordProperties> {
        this.progress$.next({done: 0, total: 0, label: VAT_LOADER_COPY.properties});
        const [ownSnapshot, formerSnapshot] = await Promise.all([
            firstValueFrom(this.propertiesService.collection(ref => ref.where('landlord.id', '==', landlordID)).get()),
            firstValueFrom(this.landlordChangeService.collection(ref => ref.where('beforeLandlord.id', '==', landlordID)).get()),
        ]);
        const properties = new Map<string, Property>();
        ownSnapshot.docs.forEach(doc => properties.set(doc.id, {...(doc.data() as Property), id: doc.id}));

        const formerIDs = [...new Set(formerSnapshot.docs
            .map(doc => (doc.data() as LandlordChange)?.property?.id)
            .filter((id): id is string => !!id && !properties.has(id)))];
        const formerDocs = await Promise.all(formerIDs.map(id => firstValueFrom(this.propertiesService.doc(id).get())));
        formerDocs.forEach(snapshot => {
            if (snapshot.exists) {
                properties.set(snapshot.id, {...(snapshot.data() as Property), id: snapshot.id});
            }
        });
        formerIDs.filter(id => !properties.has(id)).forEach(id => properties.set(id, {id} as Property));

        return {ids: [...properties.keys()].sort(), properties};
    }

    async load(landlordID: string, range: VatEngineRange, statusAtStart: VatTaxStatus, now: Date = new Date()): Promise<VatLoadedData> {
        const {ids, properties} = await this.resolveProperties(landlordID);
        const window = buildLoadWindow(range);
        const total = ids.length + 2;   // + Nachladen + Monitor/Abschluss
        this.progress$.next({done: 0, total, label: VAT_LOADER_COPY.property(0, ids.length)});

        // Self-Healing einmal je Session (Ausgaben mit Map statt Timestamp sind für Range-Queries unsichtbar)
        await this.expensesService.healExpensesWithBrokenDatesOnce();

        let done = 0;
        const slices = await runLimited(ids, PARALLEL_PROPERTIES, async propertyID => {
            const slice = await this.loadPropertySlice(propertyID, properties.get(propertyID)?.landlord?.id ?? null, window, now);
            done += 1;
            this.progress$.next({done, total, label: VAT_LOADER_COPY.property(done, ids.length)});
            return slice;
        });

        const data: VatLoadedData = {
            propertyIDs: ids,
            propertyLabels: Object.fromEntries(ids.map(id => [id, propertyLabel(properties.get(id), id)])),
            ownership: slices.flatMap(slice => slice.ownership),
            pendingChanges: slices.flatMap(slice => slice.pending),
            periods: slices.flatMap(slice => slice.periods),
            units: slices.flatMap(slice => slice.units),
            propertyProfiles: Object.fromEntries(slices.map(slice => [slice.propertyID, slice.profile])),
            bookings: mergeUniqueByID(...slices.map(slice => slice.bookings)),
            payments: mergeUniqueByID(...slices.map(slice => slice.payments)),
            priorPayments: [],
            expenses: mergeUniqueByID(...slices.map(slice => slice.expenses)),
            monitor19Input: null,
        };

        // Bookings zu Zahlungen, deren Sollstellung außerhalb des Fensters liegt (Spec 5: where('id','in',chunk ≤ 30))
        for (const ids30 of chunk(missingBookingIDs(data.payments, data.bookings), IN_CHUNK_SIZE)) {
            const snapshot = await firstValueFrom(this.bookingService.collection(ref => ref.where('id', 'in', ids30)).get());
            if (snapshot.docs.length < ids30.length) {
                console.warn('VatDataLoaderService --> Bookings zu Zahlungen nicht gefunden:', ids30.filter(id => !snapshot.docs.some(doc => doc.id === id)));
            }
            snapshot.docs.forEach(doc => data.bookings.push(toBookingLite({...(doc.data() as Booking), id: doc.id})));
        }

        // priorPayments: alle Zahlungen zu Sollstellungen mit haben ≠ 0 (Mindest-Ist, R5), ohne Dubletten zu `payments`
        const known = new Set(data.payments.map(payment => payment.id));
        for (const ids30 of chunk(bookingIDsWithHaben(data.bookings), IN_CHUNK_SIZE)) {
            const snapshot = await firstValueFrom(this.bookingPaymentsService.collection(ref => ref.where('bookingID', 'in', ids30)).get());
            snapshot.docs.forEach(doc => {
                if (!known.has(doc.id)) {
                    known.add(doc.id);
                    data.priorPayments.push(toPaymentLite({...(doc.data() as BookingPayment), id: doc.id}));
                }
            });
        }
        done += 1;
        this.progress$.next({done, total, label: VAT_LOADER_COPY.compute});

        if (needsMonitor19(statusAtStart, data.periods)) {
            this.progress$.next({done, total, label: VAT_LOADER_COPY.monitor});
            data.monitor19Input = await this.loadMonitor19(ids, range, new Map(data.periods.map(period => [period.id, period])));
        }

        this.progress$.next({done: total, total, label: VAT_LOADER_COPY.compute});
        return data;
    }

    buildEngineInput(data: VatLoadedData, params: BuildEngineInputParams): VatEngineInput {
        return {
            landlordID: params.landlordID,
            range: params.range,
            profile: params.profile,
            profileAt: createProfileAt(params.profile),
            ownership: data.ownership,
            pendingChanges: data.pendingChanges,
            periods: data.periods,
            units: data.units,
            propertyProfiles: data.propertyProfiles,
            bookings: data.bookings,
            payments: data.payments,
            priorPayments: data.priorPayments,
            expenses: data.expenses,
            adjustments: params.adjustments ?? [],
            now: params.now,
            monitor19Input: data.monitor19Input,
        };
    }

    /**
     * Quellen manueller Korrekturen nachladen (NACHTRAG_17 auf Zeilen, FORDERUNGSAUSFALL/EXCLUDE/… auf
     * Buchungen, Zahlungen, Ausgaben), wenn sie außerhalb des Ladefensters liegen – sonst meldet die
     * Engine SOURCE_DATA_INVALID („… nicht gefunden“). Query per documentId() `in` (kein Index nötig);
     * nachgeladene Zahlungen landen in `priorPayments`, damit sie in diesem Zeitraum keine IST-Zeile bilden.
     */
    async ensureAdjustmentSources(data: VatLoadedData, adjustments: VatAdjustment[]): Promise<VatLoadedData> {
        const wanted = {BOOKING: new Set<string>(), PAYMENT: new Set<string>(), EXPENSE: new Set<string>()};
        for (const adjustment of adjustments ?? []) {
            if (adjustment.targetType === 'LINE') {
                const parsed = parseLineID(adjustment.targetID);
                if (parsed) {
                    wanted[parsed.type].add(parsed.sourceID.split(':')[0]);
                }
            } else {
                wanted[adjustment.targetType].add(adjustment.targetID);
            }
        }
        const known = {
            BOOKING: new Set(data.bookings.map(booking => booking.id)),
            PAYMENT: new Set([...data.payments, ...data.priorPayments].map(payment => payment.id)),
            EXPENSE: new Set(data.expenses.map(expense => expense.id)),
        };
        const missing = (type: keyof typeof wanted): string[] => [...wanted[type]].filter(id => !known[type].has(id));
        if (!missing('BOOKING').length && !missing('PAYMENT').length && !missing('EXPENSE').length) {
            return data;
        }
        const documentID = firebase.firestore.FieldPath.documentId();
        const fetchByID = async <T>(factory: (queryFn: QueryFn) => AngularFirestoreCollection<T>, ids: string[]): Promise<T[]> => {
            const result: T[] = [];
            for (const ids30 of chunk(ids, IN_CHUNK_SIZE)) {
                const snapshot = await firstValueFrom(factory(ref => ref.where(documentID, 'in', ids30)).get());
                snapshot.docs.forEach(doc => result.push({...(doc.data() as object), id: doc.id} as T));
            }
            return result;
        };
        const [bookings, payments, expenses] = await Promise.all([
            fetchByID<Booking>(queryFn => this.bookingService.collection(queryFn), missing('BOOKING')),
            fetchByID<BookingPayment>(queryFn => this.bookingPaymentsService.collection(queryFn), missing('PAYMENT')),
            fetchByID<Expense>(queryFn => this.expensesService.collection(queryFn), missing('EXPENSE')),
        ]);
        return {
            ...data,
            bookings: mergeUniqueByID(data.bookings, bookings.map(toBookingLite)),
            priorPayments: mergeUniqueByID(data.priorPayments, payments.map(toPaymentLite).filter(payment => !known.PAYMENT.has(payment.id))),
            expenses: mergeUniqueByID(data.expenses, expenses.map(toExpenseLite)),
        };
    }

    // ------------------------------------------------------------------
    // Intern
    // ------------------------------------------------------------------

    private async loadPropertySlice(propertyID: string, currentLandlordID: string | null, window: LoadWindow, now: Date): Promise<PropertySlice> {
        this.periodsService.resetPath();   // Root-Spiegel `periods` (kein parentPath)
        const [periodsSnapshot, unitsSnapshot, bookings, payments, expenses, changesSnapshot, profileSnapshot] = await Promise.all([
            firstValueFrom(this.periodsService.collection(ref => ref.where('propertyID', '==', propertyID)).get()),
            firstValueFrom(this.unitsService.collectionWithParent(`properties/${propertyID}`).get()),
            this.loadInBatches<Booking>(queryFn => this.bookingService.collection(queryFn), ref =>
                ref.where('propertyID', '==', propertyID).where('date', '>=', window.start).where('date', '<=', window.end).orderBy('date')),
            this.loadInBatches<BookingPayment>(queryFn => this.bookingPaymentsService.collection(queryFn), ref =>
                ref.where('propertyID', '==', propertyID).where('art', 'in', [...PAYMENT_ARTS_IN]).where('date', '>=', window.start).where('date', '<=', window.end).orderBy('date')),
            this.loadInBatches<Expense>(queryFn => this.expensesService.collection(queryFn), ref =>
                ref.where('property.id', '==', propertyID).where('date', '>=', window.start).where('date', '<=', window.expenseEnd).orderBy('date')),
            firstValueFrom(this.landlordChangeService.collection(ref => ref.where('property.id', '==', propertyID)).get()),
            firstValueFrom(this.vatPropertyProfilesService.doc(propertyID).get()),
        ]);

        const changes = changesSnapshot.docs.map(doc => ({...(doc.data() as LandlordChange), id: doc.id}));
        const ownership = toOwnership(propertyID, currentLandlordID, changes, now);
        const storedProfile = profileSnapshot.exists ? (profileSnapshot.data() as PropertyVatProfile) : null;

        return {
            propertyID,
            periods: periodsSnapshot.docs.map(doc => toPeriodLite({...(doc.data() as PropertyUnitPeriod), id: doc.id})),
            units: unitsSnapshot.docs.map(doc => toUnitLite({...(doc.data() as PropertyUnit), id: doc.id}, propertyID)),
            bookings: bookings.map(toBookingLite),
            payments: payments.map(toPaymentLite),
            expenses: expenses.map(toExpenseLite),
            ownership: ownership.windows,
            pending: ownership.pending,
            // Bewusst OHNE now: das Default-Profil (kein Dokument) geht in den inputHash ein – ein now-basiertes
            // createdAt würde bei jedem Laden einen anderen Hash und damit den Fehlalarm „Daten geändert“ erzeugen.
            profile: resolvePropertyVatProfile(storedProfile, {propertyID, customerID: this.authService.customerID ?? null}),
        };
    }

    /** § 19-Monitor: Vorjahr komplett und laufendes Jahr bis zum Zeitraumbeginn – dieselbe Index-Form wie Sollstellungen. */
    private async loadMonitor19(propertyIDs: string[], range: VatEngineRange, periodsByID: ReadonlyMap<string, PeriodLite>): Promise<Monitor19Input> {
        const ranges = monitorRanges(range);
        const turnover = async (bounds: { start: Date; end: Date }): Promise<number> => {
            const perProperty = await runLimited(propertyIDs, PARALLEL_PROPERTIES, propertyID =>
                this.loadInBatches<Booking>(queryFn => this.bookingService.collection(queryFn), ref =>
                    ref.where('propertyID', '==', propertyID).where('date', '>=', bounds.start).where('date', '<=', bounds.end).orderBy('date')));
            return taxableTurnoverOfBookings(perProperty.flat().map(toBookingLite), periodsByID);
        };
        const vorjahr = await turnover(ranges.vorjahr);
        const laufendVorRange = ranges.laufend ? await turnover(ranges.laufend) : 0;
        return {vorjahr, laufendVorRange};
    }

    /** Cursor-basiert in Blöcken à 300 (Muster property-report.service.ts). */
    private async loadInBatches<T extends { id?: string }>(
        collectionFactory: (queryFn: QueryFn) => AngularFirestoreCollection<T>,
        baseQuery: (ref: any) => any,
        batchSize: number = BATCH_SIZE
    ): Promise<T[]> {
        const data: T[] = [];
        let lastDoc = null;
        let hasMore = true;
        while (hasMore) {
            const snapshot = await firstValueFrom(collectionFactory(ref => {
                let query = baseQuery(ref).limit(batchSize);
                if (lastDoc) {
                    query = query.startAfter(lastDoc);
                }
                return query;
            }).get());
            snapshot.docs.forEach(doc => data.push({...(doc.data() as object), id: doc.id} as T));
            if (snapshot.docs.length < batchSize) {
                hasMore = false;
            } else {
                lastDoc = snapshot.docs[snapshot.docs.length - 1];
            }
        }
        return data;
    }
}
